import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentDots,
  faTimes,
  faPaperPlane,
  faRobot,
  faUser,
  faSpinner,
  faTrash,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { sendChatBuilderMessage, createPageFromChat } from '../utils/apis';
import { flattenPageForAI } from '../utils/pageSnapshot';
import ChatImageCollector from './ChatImageCollector';
import ChatPageProgress from './ChatPageProgress';
import { useAppData } from './AppDataContext';
import { API_URL } from '../../config';
import { useNavigate } from 'react-router-dom';
import './BuilderChat.css';

/**
 * BuilderChat
 *
 * Props:
 *  - rows           {Array}          Current page builder rows state
 *  - pageId         {string|number}  Current page ID
 *  - onApplyActions {Function}       Callback to apply AI actions to builder state
 */
function BuilderChat({ rows, pageId, onApplyActions }) {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [siteContext, setSiteContext] = useState(null); // { site_name, business_type, key_services, language }
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hi! I'm your AI builder assistant. Tell me what you'd like to change — text, colors, layout, add sections, and more. I'll generate content relevant to your website.",
    },
  ]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { subdomain } = useAppData();
  const navigate = useNavigate();
  const loadingIntervalRef = useRef(null);

  // ── Fetch site context once on mount ──────────────────────────
  useEffect(() => {
    if (!subdomain) return;

    const cacheKey = `builder-chat-site-context:${subdomain}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached?.data && Date.now() - (cached.timestamp || 0) < 10 * 60 * 1000) {
          setSiteContext(cached.data);
          return;
        }
      } catch {
        sessionStorage.removeItem(cacheKey);
      }
    }

    fetch(`${API_URL}/meta-data?account_key=${subdomain}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        identifiers: ['site_name', 'business_type', 'key_services', 'language_chosen'],
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const ctx = {};
        data.forEach((item) => {
          if (item.identifier === 'site_name')       ctx.site_name     = item.data;
          if (item.identifier === 'business_type')   ctx.business_type = item.data;
          if (item.identifier === 'key_services')    ctx.key_services  = item.data;
          if (item.identifier === 'language_chosen') ctx.language      = item.data;
        });
        if (Object.keys(ctx).length > 0) {
          setSiteContext(ctx);
          sessionStorage.setItem(cacheKey, JSON.stringify({
            timestamp: Date.now(),
            data: ctx,
          }));
        }
      })
      .catch(() => {
        // Non-fatal — AI will still work without site context
      });
  }, [subdomain]);

  // ── Auto-scroll ────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  // ── Focus input on open ────────────────────────────────────────
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
    };
  }, []);

  // ── Build history for AI (role + content only, last 12) ────────
  const buildHistory = useCallback((msgs) => {
    return msgs
      .filter((m) => {
        // Include all user messages
        if (m.role === 'user') return true;
        // Include assistant messages that have text content (not just image collectors)
        if (m.role === 'assistant' && !m.needsImages && !m.creatingPage && m.content) return true;
        return false;
      })
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.content }));
  }, []);

  // ── Send message ───────────────────────────────────────────────
  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);
    setLoadingStage('Understanding your request...');
    setLoadingProgress(18);

    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
    }

    const stages = [
      { label: 'Understanding your request...', progress: 18 },
      { label: 'Checking builder capabilities...', progress: 42 },
      { label: 'Preparing exact changes...', progress: 68 },
      { label: 'Finalizing response...', progress: 86 },
    ];

    let stageIndex = 0;
    loadingIntervalRef.current = setInterval(() => {
      stageIndex = Math.min(stageIndex + 1, stages.length - 1);
      setLoadingStage(stages[stageIndex].label);
      setLoadingProgress(stages[stageIndex].progress);
    }, 1200);

    try {
      const pageSnapshot = flattenPageForAI(rows);

      const data = await sendChatBuilderMessage({
        message: trimmed,
        pageId,
        history: buildHistory(messages), // full conversation history
        pageSnapshot,
        siteContext: siteContext || {},
      });

      const { actions = [], summary = 'Done.', needsImages, pendingAction, imageRequests } = data;
      setLoadingStage('Applying changes...');
      setLoadingProgress(96);

      // ── Case 1: AI needs images ──────────────────────────────
      if (needsImages && pendingAction && imageRequests?.length) {
        setMessages([
          ...updatedMessages,
          {
            role: 'assistant',
            content: summary,
            needsImages: true,
            pendingAction,
            imageRequests,
          },
        ]);
        setLoading(false);
        return;
      }

      // ── Case 2: CREATE_PAGE — dispatch queue job ─────────────
      const createPageAction = actions.find((a) => a.type === 'CREATE_PAGE');
      if (createPageAction) {
        const pageTitle   = createPageAction.suggested_title || 'New Page';
        const description = createPageAction.description || summary;

        // Add the message immediately — show spinner while API call runs
        const msgIndex = updatedMessages.length;
        const startMsg = {
          role:         'assistant',
          content:      `I'll create the "${pageTitle}" page now. Designing structure with AI...`,
          creatingPage: true,
          pageTitle,
          trackingId:   null,
          pageId:       null,
        };

        setMessages([...updatedMessages, startMsg]);
        setLoading(false);

        createPageFromChat({
          description,
          accountKey:  subdomain,
          siteContext: siteContext || {},
        })
          .then((result) => {
            // Page structure is in DB — update message with trackingId + pageId
            // User can open the page immediately; content enrichment runs in background
            setMessages((prev) =>
              prev.map((m, i) =>
                i === msgIndex
                  ? {
                      ...m,
                      content:    `"${result.page_title || pageTitle}" page created! AI is now generating content for each section.`,
                      trackingId: result.tracking_id,
                      pageId:     result.page_id,
                      pageTitle:  result.page_title || pageTitle,
                    }
                  : m
              )
            );
          })
          .catch((err) => {
            setMessages((prev) =>
              prev.map((m, i) =>
                i === msgIndex
                  ? {
                      ...m,
                      creatingPage: false,
                      content:      `Failed to create page: ${err.message}`,
                      isError:      true,
                    }
                  : m
              )
            );
          });

        return;
      }

      // ── Case 3: Normal actions ───────────────────────────────
      if (actions.length > 0 && onApplyActions) {
        onApplyActions(actions);
      }

      setMessages([
        ...updatedMessages,
        {
          role: 'assistant',
          content: summary,
          actions,
          appliedCount: actions.filter((a) => a.type !== 'CLARIFY').length,
        },
      ]);
    } catch (err) {
      console.error('BuilderChat error:', err);
      setMessages([
        ...updatedMessages,
        { role: 'assistant', content: 'Something went wrong. Please try again.', isError: true },
      ]);
    } finally {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
      setLoading(false);
      setLoadingStage('');
      setLoadingProgress(0);
    }
  }, [input, loading, messages, rows, pageId, onApplyActions, siteContext, buildHistory]);

  // ── Image collection complete ──────────────────────────────────
  const handleImagesCollected = useCallback(
    (resolvedAction, msgIndex) => {
      if (onApplyActions && resolvedAction?.type) {
        const actionToApply = JSON.parse(JSON.stringify(resolvedAction));
        delete actionToApply.needsImages;
        delete actionToApply.pendingAction;
        delete actionToApply.imageRequests;
        delete actionToApply.summary;

        console.log('[AI] handleImagesCollected dispatching:', actionToApply);
        onApplyActions([actionToApply]);
      }
      setMessages((prev) =>
        prev.map((m, i) =>
          i === msgIndex
            ? { ...m, needsImages: false, content: 'Done! Images applied to the page.', appliedCount: 1 }
            : m
        )
      );
    },
    [onApplyActions]
  );

  const handleImagesCancelled = useCallback((msgIndex) => {
    setMessages((prev) =>
      prev.map((m, i) =>
        i === msgIndex
          ? { ...m, needsImages: false, content: "Cancelled. Let me know if you'd like to try again." }
          : m
      )
    );
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: siteContext?.site_name
          ? `Chat cleared. I'm ready to help you build ${siteContext.site_name}. What would you like to change?`
          : 'Chat cleared. What would you like to change on this page?',
      },
    ]);
  };

  const suggestions = [
    'Create an About Us page',
    'Create a Contact page',
    'Create a Services page',
    'Add a gallery section with my images',
    'Change the heading text color to blue',
    'Make the hero background dark navy',
    'Ask in Hindi, Spanish, or your own language',
  ];

  return (
    <>
      {/* Floating toggle button */}
      <button
        className={`builder-chat-toggle ${isOpen ? 'chat-open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        title="AI Builder Assistant"
        aria-label="Toggle AI chat assistant"
      >
        {isOpen ? (
          <FontAwesomeIcon icon={faChevronDown} />
        ) : (
          <>
            <FontAwesomeIcon icon={faCommentDots} />
            <span className="chat-toggle-label">AI Assistant</span>
          </>
        )}
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div className="builder-chat-panel" role="dialog" aria-label="AI Builder Assistant">
          {/* Header */}
          <div className="builder-chat-header">
            <div className="chat-header-left">
              <FontAwesomeIcon icon={faRobot} className="chat-robot-icon" />
              <div>
                <div className="chat-header-title">
                  AI Builder Assistant
                  {siteContext?.site_name && (
                    <span className="chat-site-badge">{siteContext.site_name}</span>
                  )}
                </div>
                <div className="chat-header-subtitle">
                  {siteContext?.business_type
                    ? `${siteContext.business_type} · Describe changes in your language`
                    : 'Describe changes in your language'}
                </div>
              </div>
            </div>
            <div className="chat-header-actions">
              <button className="chat-icon-btn" onClick={clearChat} title="Clear chat" aria-label="Clear chat">
                <FontAwesomeIcon icon={faTrash} />
              </button>
              <button className="chat-icon-btn" onClick={() => setIsOpen(false)} title="Close" aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="builder-chat-messages" role="log" aria-live="polite">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`chat-message chat-message--${msg.role} ${msg.isError ? 'chat-message--error' : ''}`}
              >
                <div className="chat-message-avatar">
                  <FontAwesomeIcon icon={msg.role === 'assistant' ? faRobot : faUser} />
                </div>
                <div className="chat-message-body">
                  <p className="chat-message-text">{msg.content}</p>

                  {msg.appliedCount > 0 && (
                    <div className="chat-actions-badge">
                      ✓ {msg.appliedCount} change{msg.appliedCount !== 1 ? 's' : ''} applied
                    </div>
                  )}

                  {/* Image collector */}
                  {msg.needsImages && msg.pendingAction && msg.imageRequests && (
                    <ChatImageCollector
                      imageRequests={msg.imageRequests}
                      pendingAction={msg.pendingAction}
                      accountKey={subdomain}
                      siteContext={siteContext || {}}
                      onComplete={(resolvedAction) => handleImagesCollected(resolvedAction, idx)}
                      onCancel={() => handleImagesCancelled(idx)}
                    />
                  )}

                  {/* Page creation progress */}
                  {msg.creatingPage && (
                    msg.trackingId ? (
                      <ChatPageProgress
                        trackingId={msg.trackingId}
                        pageTitle={msg.pageTitle || 'New Page'}
                        pageId={msg.pageId}
                        onComplete={({ pageId: completedPageId }) => {
                          setMessages((prev) =>
                            prev.map((m, i) =>
                              i === idx ? { ...m, creatingPage: false } : m
                            )
                          );
                        }}
                        onNavigate={(completedPageId) => {
                          navigate(`/page-builder/${completedPageId}`);
                          setIsOpen(false);
                        }}
                      />
                    ) : (
                      // Waiting for the API to return a tracking ID
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        background: '#f5f3ff',
                        border: '1.5px solid #a5b4fc',
                        borderRadius: '10px',
                        marginTop: '6px',
                        fontSize: '12.5px',
                        color: '#4f46e5',
                        fontWeight: '500',
                      }}>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Designing page structure with AI...
                      </div>
                    )
                  )}

                  {/* CLARIFY bubbles */}
                  {msg.actions &&
                    msg.actions
                      .filter((a) => a.type === 'CLARIFY')
                      .map((a, i) => (
                        <div key={i} className="chat-clarify-bubble">{a.message}</div>
                      ))}
                </div>
              </div>
            ))}

            {loading && (
              <div className="chat-message chat-message--assistant">
                <div className="chat-message-avatar">
                  <FontAwesomeIcon icon={faRobot} />
                </div>
                <div className="chat-message-body">
                  <div style={{ minWidth: '240px' }}>
                    <div style={{ fontSize: '12.5px', color: '#4b5563', marginBottom: '8px', fontWeight: 500 }}>
                      {loadingStage || 'Working...'}
                    </div>
                    <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '999px', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${loadingProgress}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #4f46e5, #22c55e)',
                          borderRadius: '999px',
                          transition: 'width 240ms ease',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>
                      {loadingProgress}% complete
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggestion chips */}
          {messages.length === 1 && (
            <div className="chat-suggestions">
              {suggestions.map((s, i) => (
                <button key={i} className="chat-suggestion-chip" onClick={() => { setInput(s); inputRef.current?.focus(); }}>
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="builder-chat-input-area">
            <textarea
              ref={inputRef}
              className="builder-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                siteContext?.site_name
                  ? `Ask me to change anything on ${siteContext.site_name} in your language...`
                  : 'e.g. change the hero, add a gallery, or write in your own language...'
              }
              rows={2}
              disabled={loading}
              aria-label="Chat message input"
            />
            <button
              className="builder-chat-send"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              aria-label="Send message"
            >
              {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
            </button>
          </div>

          <div className="chat-footer-hint">Press Enter to send · Shift+Enter for new line</div>
        </div>
      )}
    </>
  );
}

export default BuilderChat;
